import {
    Button,
    Flex,
    useStyles,
    Avatar,
    Caption,
    pxToRem,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import * as S from './ProductLocation.style';
import type { LocationDataType } from '@modules/professional/type/contact-page-types';

import { MapPinFill } from '@archipro-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';
import {
    getFullAddress,
    getLocationSuburbText,
} from '~/modules/professional/util/contact-page-helper';
import { useTracker } from '@archipro-website/tracker';
import { useNavigate } from 'react-router';
import type { LocationProfessional } from '../../util/get-professional-locations';
import { useFormatDistance } from '~/modules/location/hook/use-format-distance';

interface ProductLocationProps {
    location: LocationDataType;
    professional: LocationProfessional;
    masterProfessional: LocationProfessional;
}

export const ProductLocation = ({
    location,
    professional,
    masterProfessional,
}: ProductLocationProps) => {
    const { onRevealClick: onLocationRevealClick } = useClickToReveal({
        revealType: 'Location',
        revealKey: `professional-${masterProfessional.ID}`,
        revealSubKey: location.ID.toString(),
    });

    const { onRevealClick: onPhoneRevealClick } = useClickToReveal({
        revealType: 'Phone',
        revealKey: `professional-${masterProfessional.ID}-${location.ID}`,
        revealSubKey: location.ID.toString(),
        professionalId: masterProfessional.ID,
    });

    const [expanded, setExpanded] = useState(false);
    const { css } = useStyles({ expanded });
    const formatDistance = useFormatDistance();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const navigate = useNavigate();

    const locationRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!expanded || !locationRef.current) return;
        locationRef.current.scrollIntoView?.({
            behavior: 'smooth',
            inline: 'center',
        });
    }, [expanded]);

    return (
        <Flex
            className={css(S.ContentContainer)}
            column
            onClick={() => {
                if (expanded) return;
                setExpanded(true);
                onLocationRevealClick();
                onPhoneRevealClick();
            }}
            ref={locationRef}
        >
            <Flex className={css(S.LocationBaseInfoContainer)}>
                <Avatar
                    image={{
                        src: professional.CompanyLogo ?? '',
                        alt: `${professional.Title} company logo`,
                    }}
                    size={48}
                    shape={'square'}
                    imageFit={'contain'}
                    variables={{
                        avatarImageBackgroundColor:
                            professional.LogoBackgroundColor ?? undefined,
                        borderWidth: '0',
                        avatarBackgroundColor: 'unset',
                    }}
                />
                <Flex
                    className={css(S.ProfessionalInfoContainer)}
                    column
                    space="between"
                >
                    <Caption
                        variant={'03'}
                        weight={'medium'}
                        className={css(S.ProfessionalTitle)}
                    >
                        {professional.Title}
                    </Caption>
                    <Caption variant="04" className={css(S.Address)}>
                        {getLocationSuburbText(location)}
                    </Caption>
                </Flex>
                <Flex
                    className={css(S.LocationInfoContainer)}
                    column
                    space="between"
                >
                    <Flex vAlign="center">
                        {!!location.distance && (
                            <>
                                <MapPinFill
                                    variables={{
                                        '1xSize': pxToRem(20),
                                        color: '#000',
                                    }}
                                />
                                <Caption
                                    variant={'03'}
                                    className={css(S.Distance)}
                                >
                                    {formatDistance(location.distance)}
                                </Caption>
                            </>
                        )}
                    </Flex>

                    <Button
                        color="dark"
                        variant="text"
                        size={14}
                        variables={{
                            buttonPadding: 0,
                        }}
                        className={css(S.ExpandButton)}
                        onClick={(e, _) => {
                            if (!expanded) return;
                            setExpanded(false);
                            e.stopPropagation();
                        }}
                    >
                        {`${expanded ? 'Hide' : 'Show'}${
                            mobile ? '' : ' Location'
                        } Details`}
                    </Button>
                </Flex>
            </Flex>

            {expanded && (
                <Flex className={css(S.LocationDetailContainer)} column>
                    <Caption
                        variant={'03'}
                        weight={'medium'}
                        className={css(S.LocationType)}
                    >
                        {location.Type}
                    </Caption>
                    <Caption variant="03" className={css(S.LocationInfo)}>
                        {getFullAddress(location)}
                    </Caption>
                    {location.Phone && (
                        <Caption
                            variant="03"
                            className={css(S.LocationInfo)}
                            as="a"
                            href={`tel:${location.Phone.replaceAll(' ', '')}`}
                            onClick={() => {
                                tracker.log(`PhoneSubmit`, {
                                    url: new URL(window.location.href),
                                    label: location.Phone,
                                });
                            }}
                        >
                            {location.Phone}
                        </Caption>
                    )}

                    <Button
                        color="dark"
                        variant="text"
                        size={16}
                        variables={{
                            buttonPadding: 0,
                        }}
                        onClick={async () => {
                            await tracker.log(
                                'WhereToBuyLocationViewOnMapClick',
                                {
                                    url: new URL(window.location.href),
                                },
                            );
                            navigate(
                                `/professional/${
                                    masterProfessional.URLSegment
                                }/contact?ID=${location.ID}${
                                    mobile ? '&showMap=1' : ''
                                }`,
                            );
                        }}
                        styles={{
                            width: 'fit-content',
                            marginTop: pxToRem(5),
                            textUnderlineOffset: pxToRem(3),
                        }}
                    >
                        View on Map
                    </Button>
                </Flex>
            )}
        </Flex>
    );
};
